import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import { renderAutoCompleteLocaleText } from 'utils';
import { Autocomplete, CircularProgress, FormLabel, TextField, svgIconClasses } from '@mui/material';

type BookOption = {
  id: number;
  title: string;
};

type BookAutocompleteProps<T extends BookOption> = {
  id: string;
  label: ReactNode;
  options: T[];
  value: T | null;
  loading: boolean;
  onChange: (value: T | null) => void;
  error?: boolean;
  helperText?: ReactNode;
};

const BookAutocomplete = <T extends BookOption>({
  id,
  label,
  options,
  value,
  loading,
  onChange,
  error = false,
  helperText,
}: BookAutocompleteProps<T>) => {
  const intl = useIntl();

  return (
    <>
      <FormLabel htmlFor={id}>{label}</FormLabel>
      <Autocomplete
        id={id}
        size="small"
        options={options}
        loading={loading}
        value={value}
        {...renderAutoCompleteLocaleText(intl)}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={(option) => option.title}
        isOptionEqualToValue={(option, currentValue) => option.id === currentValue.id}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            fullWidth
            size="small"
            error={error}
            helperText={helperText}
            sx={{
              '& .MuiButtonBase-root': {
                border: 'none',
                backgroundColor: 'inherit',
                width: '2.25rem',
                height: '2.25rem',
                padding: '0.25rem',
                [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                '&:hover': { bgcolor: 'action.hover' },
              },
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </>
  );
};

export default BookAutocomplete;
